import { useMemo } from "react";
import { ProductSearchFilters, SaleFilter, StockFilter, Supplier } from "../types/api";
import { useLocations } from "src/hooks/useLocations";
import { FilterChip, FilterChipRow, PickerOption } from "./filter-chip.component";

export const EMPTY_PRODUCT_FILTERS: ProductSearchFilters = {
  stock: "all",
  sale: "all",
  supplierGuid: null,
  location: null,
};

export const countActiveProductFilters = (filters: ProductSearchFilters): number =>
  (filters.stock !== "all" ? 1 : 0) +
  (filters.sale && filters.sale !== "all" ? 1 : 0) +
  (filters.supplierGuid ? 1 : 0) +
  (filters.location ? 1 : 0);

const STOCK_OPTIONS: PickerOption[] = [
  { value: "in", label: "In stock" },
  { value: "out", label: "Out of stock" },
];

// "On sale" is the product's `sale` tag, which the edit page keeps in step
// with the variants' previous/retail prices.
const SALE_OPTIONS: PickerOption[] = [
  { value: "on", label: "Yes" },
  { value: "off", label: "No" },
];

interface Props {
  filters: ProductSearchFilters;
  onChange: (filters: ProductSearchFilters) => void;
  suppliers: Supplier[];
}

// Always-visible filter chips beneath the product search bar. Everything here
// is applied server-side as WHERE clauses, in combination with whatever is
// typed in the search bar.
const ProductFilterChips = (props: Props) => {
  const { filters, onChange, suppliers } = props;
  const { locations } = useLocations();

  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((s: Supplier) => !!s.guid)
        .map((s: Supplier) => ({ value: s.guid as string, label: s.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [suppliers]
  );
  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l, label: l })),
    [locations]
  );

  const activeCount = countActiveProductFilters(filters);

  return (
    <FilterChipRow
      testId="product-filter-chips"
      clearTestId="clear-product-filters"
      onClear={activeCount > 0 ? () => onChange({ ...EMPTY_PRODUCT_FILTERS }) : undefined}
    >
      <FilterChip
        name="Stock"
        anyLabel="All"
        value={filters.stock === "all" ? null : filters.stock}
        options={STOCK_OPTIONS}
        onChange={(v) => onChange({ ...filters, stock: (v as StockFilter) || "all" })}
        testId="filter-chip-stock"
      />
      <FilterChip
        name="Supplier"
        anyLabel="Any"
        value={filters.supplierGuid}
        options={supplierOptions}
        onChange={(v) => onChange({ ...filters, supplierGuid: v })}
        searchable
        searchPlaceholder="Find a supplier..."
        testId="filter-chip-supplier"
      />
      <FilterChip
        name="Location"
        anyLabel="Any"
        value={filters.location}
        options={locationOptions}
        onChange={(v) => onChange({ ...filters, location: v })}
        searchable
        searchPlaceholder="Find a location..."
        testId="filter-chip-location"
      />
      <FilterChip
        name="On Sale"
        anyLabel="All"
        value={!filters.sale || filters.sale === "all" ? null : filters.sale}
        options={SALE_OPTIONS}
        onChange={(v) => onChange({ ...filters, sale: (v as SaleFilter) || "all" })}
        testId="filter-chip-sale"
      />
    </FilterChipRow>
  );
};

export default ProductFilterChips;
